import React, { createContext, useContext, useState, useEffect } from "react";
import { logout as apiLogout } from "../api/AuthApi";
import axios from "axios";
import { Spin } from "antd";

// Create the authentication context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any child component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  // Helper function to parse JWT token and get expiry time
  const getTokenExpiry = (token) => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.exp) {
        // Convert expiry time to milliseconds
        return payload.exp * 1000;
      }
    } catch (error) {
      console.error("Error parsing token:", error);
    }
    return null;
  };

  useEffect(() => {
    // Restore auth state from sessionStorage on first load
    const token = sessionStorage.getItem("authToken");

    if (token) {
      // Optionally compute expiry if needed
      const expiry = getTokenExpiry(token);
      setTokenExpiry(expiry);

      setUser({ token });
    } else {
      setUser(null);
    }

    setLoading(false);
  }, []);

  // Login function
  const login = (userData) => {
    // Store in both sessionStorage and localStorage
    sessionStorage.setItem("authToken", userData.token);
    // localStorage.setItem("authToken", userData.token);

    // Store user data in state (without refresh token)
    setUser({ token: userData.token });
  };

  // Logout function
  const logout = async () => {
    try {
      // Call the API to invalidate the refresh token on the server
      // The server will clear the HTTP-only cookie
      await apiLogout();
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      // Clear both storage locations and reset state
      sessionStorage.removeItem("authToken");
      // localStorage.removeItem("authToken");
      setTokenExpiry(null);
      setUser(null);
    }
  };

  // Check if user has a specific role
  const hasRole = (roles) => {
    // With token-only auth, treat any authenticated user as authorized
    return !!user;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    // Derive authentication status from presence of user object
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <Spin spinning={true} fullscreen /> : children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
